/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Textes juridiques des deux sites : politique de confidentialite et conditions
 * generales d'utilisation.
 *
 * Les deux entites publient les memes documents ; seuls changent la raison
 * sociale, l'adresse, l'autorite de controle et le droit applicable. Plutot que
 * d'entretenir quatre jeux de textes qui divergeraient a la premiere retouche,
 * un seul gabarit porte des jetons `{entite}`, `{adresse}`... que `buildLegal`
 * remplit avec la fiche de l'entite demandee.
 *
 * Les valeurs encadrees par des crochets restent a completer : ce sont des
 * numeros officiels que le site ne peut pas inventer.
 *
 * Ces textes sont une base professionnelle, pas un avis juridique : ils doivent
 * etre relus par un conseil avant mise en ligne definitive.
 */

/** Fiches d'identite des deux entites editrices. */
export const ENTITIES = {
  uk: {
    entite: 'Prenim Ltd',
    // L'adresse est traduite comme le reste : la page anglaise affichait
    // « Royaume-Uni » deux paragraphes apres avoir ecrit « United Kingdom ».
    adresse: {
      fr: '30 Button Hill, Leeds, LS7 3DA, Royaume-Uni',
      en: '30 Button Hill, Leeds, LS7 3DA, United Kingdom',
      es: '30 Button Hill, Leeds, LS7 3DA, Reino Unido',
      ar: '30 Button Hill, Leeds, LS7 3DA, المملكة المتحدة',
    },
    email: 'hello@prenim.co.uk',
    telephone: '+44 113 880 5181',
    immatriculation: "[numéro d'immatriculation Companies House]",
    autorite: {
      fr: "l'Information Commissioner's Office (ICO), autorité de contrôle britannique",
      en: "the Information Commissioner's Office (ICO), the UK supervisory authority",
      es: "la Information Commissioner's Office (ICO), autoridad de control británica",
      ar: "مكتب مفوض المعلومات (ICO)، الهيئة الرقابية البريطانية",
    },
    loi: {
      fr: "le droit d'Angleterre et du Pays de Galles",
      en: 'the laws of England and Wales',
      es: 'el derecho de Inglaterra y Gales',
      ar: 'قانون إنجلترا وويلز',
    },
    cadre: {
      fr: 'le UK GDPR et le Data Protection Act 2018',
      en: 'the UK GDPR and the Data Protection Act 2018',
      es: 'el UK GDPR y la Data Protection Act 2018',
      ar: 'اللائحة البريطانية لحماية البيانات وقانون حماية البيانات 2018',
    },
    juridiction: {
      fr: "les tribunaux d'Angleterre et du Pays de Galles",
      en: 'the courts of England and Wales',
      es: 'los tribunales de Inglaterra y Gales',
      ar: 'محاكم إنجلترا وويلز',
    },
  },
  tchad: {
    entite: 'Prenim Tchad',
    adresse: {
      fr: "694 Avenue de Bezo, N'Djamena, Tchad",
      en: "694 Avenue de Bezo, N'Djamena, Chad",
      es: "694 Avenue de Bezo, Yamena, Chad",
      ar: "694 شارع بيزو، نجامينا، تشاد",
    },
    email: 'hello@prenim.co.uk',
    telephone: '+235 85 20 67 61',
    immatriculation: '[numéro RCCM]',
    autorite: {
      fr: "l'Autorité Nationale de Sécurité Électronique et de Certification (ANSICE)",
      en: 'the Chadian national authority for electronic security and certification (ANSICE)',
      es: 'la autoridad nacional chadiana de seguridad electrónica y certificación (ANSICE)',
      ar: 'الهيئة الوطنية للأمن الإلكتروني والتصديق (ANSICE)',
    },
    loi: {
      fr: 'le droit tchadien',
      en: 'Chadian law',
      es: 'el derecho chadiano',
      ar: 'القانون التشادي',
    },
    cadre: {
      fr: "la loi tchadienne n° 007/PR/2015 sur la protection des données à caractère personnel",
      en: 'Chadian law no. 007/PR/2015 on the protection of personal data',
      es: 'la ley chadiana n.º 007/PR/2015 sobre protección de datos personales',
      ar: 'القانون التشادي رقم 007/PR/2015 بشأن حماية البيانات الشخصية',
    },
    juridiction: {
      fr: "les tribunaux compétents de N'Djamena",
      en: "the competent courts of N'Djamena",
      es: "los tribunales competentes de Yamena",
      ar: 'المحاكم المختصة في نجامينا',
    },
  },
};

const PRIVACY = {
  slug: 'privacy',
  title: {
    fr: 'Politique de confidentialité',
    en: 'Privacy Policy',
    es: 'Política de privacidad',
    ar: 'سياسة الخصوصية',
  },
  lead: {
    fr:
      "Cette politique explique quelles données personnelles {entite} recueille lorsque vous " +
      'utilisez ce site, pourquoi elle les traite, combien de temps elle les conserve et ' +
      'quels droits vous pouvez exercer.',
    en:
      'This policy explains what personal data {entite} collects when you use this site, why ' +
      'it processes that data, how long it keeps it and what rights you can exercise.',
    es:
      'Esta política explica qué datos personales recoge {entite} cuando usted utiliza este ' +
      'sitio, por qué los trata, cuánto tiempo los conserva y qué derechos puede ejercer.',
    ar:
      'توضح هذه السياسة البيانات الشخصية التي تجمعها {entite} عند استخدامك لهذا الموقع، ' +
      'وسبب معالجتها، ومدة الاحتفاظ بها، والحقوق التي يمكنك ممارستها.',
  },
  sections: [
    {
      key: 'responsable',
      title: {
        fr: 'Responsable du traitement',
        en: 'Data controller',
        es: 'Responsable del tratamiento',
        ar: 'المسؤول عن المعالجة',
      },
      body: {
        fr:
          "{entite}, dont le siège est situé {adresse}, est responsable du traitement des " +
          'données personnelles collectées via ce site. Pour toute question relative à vos ' +
          'données, écrivez à {email} ou appelez le {telephone}.',
        en:
          '{entite}, registered at {adresse}, is the controller of the personal data ' +
          'collected through this site. For any question about your data, write to {email} ' +
          'or call {telephone}.',
        es:
          '{entite}, con domicilio en {adresse}, es responsable del tratamiento de los datos ' +
          'personales recogidos a través de este sitio. Para cualquier consulta sobre sus ' +
          'datos, escriba a {email} o llame al {telephone}.',
        ar:
          '{entite}، ومقرها {adresse}، هي المسؤولة عن معالجة البيانات الشخصية المجمعة عبر هذا ' +
          'الموقع. لأي سؤال بخصوص بياناتك، راسلنا على {email} أو اتصل بالرقم {telephone}.',
      },
    },
    {
      key: 'donnees',
      title: {
        fr: 'Données que nous collectons',
        en: 'Data we collect',
        es: 'Datos que recogemos',
        ar: 'البيانات التي نجمعها',
      },
      body: {
        fr:
          'Nous ne collectons que les données que vous nous transmettez volontairement : nom, ' +
          'adresse électronique, numéro de téléphone, société et contenu du message lorsque ' +
          'vous remplissez le formulaire de contact ou une candidature. Aucun compte ' +
          "utilisateur n'est créé et nous ne demandons jamais de donnée bancaire sur ce site.",
        en:
          'We only collect the data you send us voluntarily: name, email address, phone ' +
          'number, company and message content when you fill in the contact form or apply for ' +
          'a role. No user account is created, and we never ask for payment details on this ' +
          'site.',
        es:
          'Solo recogemos los datos que usted nos envía voluntariamente: nombre, correo ' +
          'electrónico, teléfono, empresa y contenido del mensaje cuando rellena el ' +
          'formulario de contacto o una candidatura. No se crea ninguna cuenta de usuario y ' +
          'nunca solicitamos datos bancarios en este sitio.',
        ar:
          'نجمع فقط البيانات التي ترسلها إلينا طوعًا: الاسم والبريد الإلكتروني ورقم الهاتف ' +
          'والشركة ومحتوى الرسالة عند ملء نموذج الاتصال أو طلب التوظيف. لا يتم إنشاء حساب ' +
          'مستخدم، ولا نطلب أبدًا بيانات مصرفية على هذا الموقع.',
      },
    },
    {
      key: 'finalites',
      title: {
        fr: 'Finalités et bases légales',
        en: 'Purposes and legal bases',
        es: 'Finalidades y bases jurídicas',
        ar: 'الأغراض والأسس القانونية',
      },
      body: {
        fr:
          'Vos données servent à répondre à votre demande, à préparer une proposition ' +
          "commerciale et à instruire une candidature. La base légale est l'exécution de " +
          'mesures précontractuelles à votre demande, ou notre intérêt légitime à répondre ' +
          'aux sollicitations qui nous sont adressées. Nous ne faisons ni prospection de ' +
          'masse, ni profilage, ni décision automatisée.',
        en:
          'Your data is used to answer your request, prepare a commercial proposal and process ' +
          'a job application. The legal basis is the performance of pre-contractual steps at ' +
          'your request, or our legitimate interest in replying to enquiries addressed to us. ' +
          'We carry out no mass prospecting, no profiling and no automated decision-making.',
        es:
          'Sus datos sirven para responder a su solicitud, preparar una propuesta comercial e ' +
          'instruir una candidatura. La base jurídica es la ejecución de medidas ' +
          'precontractuales a petición suya, o nuestro interés legítimo en responder a las ' +
          'consultas recibidas. No realizamos prospección masiva, ni elaboración de perfiles, ' +
          'ni decisiones automatizadas.',
        ar:
          'تُستخدم بياناتك للرد على طلبك، وإعداد عرض تجاري، ودراسة طلب التوظيف. الأساس ' +
          'القانوني هو تنفيذ إجراءات ما قبل التعاقد بناءً على طلبك، أو مصلحتنا المشروعة في ' +
          'الرد على الاستفسارات الموجهة إلينا. لا نقوم بأي تسويق جماعي أو تنميط أو قرارات آلية.',
      },
    },
    {
      key: 'cookies',
      title: {
        fr: 'Cookies et mesure d’audience',
        en: 'Cookies and analytics',
        es: 'Cookies y medición de audiencia',
        ar: 'ملفات تعريف الارتباط وقياس الزيارات',
      },
      body: {
        fr:
          'Ce site ne dépose aucun cookie publicitaire ni traceur tiers. Seul un stockage ' +
          'local est utilisé pour mémoriser la langue que vous avez choisie ; il reste dans ' +
          "votre navigateur et ne nous est jamais transmis. Aucun consentement n'est donc " +
          'requis à ce titre.',
        en:
          'This site sets no advertising cookies and no third-party trackers. Only local ' +
          'storage is used, to remember the language you selected; it stays in your browser ' +
          'and is never sent to us. No consent banner is therefore required.',
        es:
          'Este sitio no instala cookies publicitarias ni rastreadores de terceros. Solo se ' +
          'utiliza almacenamiento local para recordar el idioma elegido; permanece en su ' +
          'navegador y nunca se nos transmite. Por tanto, no se requiere consentimiento.',
        ar:
          'لا يضع هذا الموقع أي ملفات تعريف ارتباط إعلانية أو أدوات تتبع من أطراف ثالثة. ' +
          'يُستخدم التخزين المحلي فقط لتذكر اللغة التي اخترتها؛ ويبقى في متصفحك ولا يُرسل ' +
          'إلينا أبدًا. لذلك لا يلزم أي موافقة.',
      },
    },
    {
      key: 'partage',
      title: {
        fr: 'Partage et sous-traitants',
        en: 'Sharing and processors',
        es: 'Comunicación y encargados',
        ar: 'المشاركة والمعالجون',
      },
      body: {
        fr:
          'Vos données ne sont ni vendues, ni louées, ni échangées. Elles sont accessibles aux ' +
          'seules personnes de {entite} qui en ont besoin, et à nos prestataires techniques ' +
          "d'hébergement et d'acheminement des courriels, liés par contrat et tenus au même " +
          'niveau de protection. Une communication peut intervenir si la loi nous y oblige.',
        en:
          'Your data is never sold, rented or exchanged. It is accessible only to the people ' +
          'at {entite} who need it, and to our technical providers for hosting and email ' +
          'delivery, who are bound by contract and held to the same level of protection. ' +
          'Disclosure may occur where the law requires it.',
        es:
          'Sus datos no se venden, alquilan ni intercambian. Solo son accesibles al personal ' +
          'de {entite} que los necesita y a nuestros proveedores técnicos de alojamiento y ' +
          'envío de correo, vinculados por contrato y sujetos al mismo nivel de protección. ' +
          'Podrá haber comunicación si la ley lo exige.',
        ar:
          'لا تُباع بياناتك ولا تُؤجَّر ولا تُتبادل. لا يطّلع عليها سوى العاملين في {entite} ' +
          'الذين يحتاجونها، ومزودي الخدمات التقنية للاستضافة وإرسال البريد، وهم ملزمون بعقد ' +
          'وبنفس مستوى الحماية. قد يحدث الإفصاح إذا ألزمنا القانون بذلك.',
      },
    },
    {
      key: 'transferts',
      title: {
        fr: 'Transferts internationaux',
        en: 'International transfers',
        es: 'Transferencias internacionales',
        ar: 'عمليات النقل الدولية',
      },
      body: {
        fr:
          'Prenim opère entre le Royaume-Uni et le Tchad : vos données peuvent circuler entre ' +
          'ces deux pays au sein de nos équipes. Nos serveurs sont situés en Europe. Tout ' +
          "transfert hors du pays de collecte s'accompagne de garanties contractuelles " +
          'appropriées.',
        en:
          'Prenim operates between the United Kingdom and Chad: your data may move between ' +
          'those two countries within our teams. Our servers are located in Europe. Any ' +
          'transfer outside the country of collection is covered by appropriate contractual ' +
          'safeguards.',
        es:
          'Prenim opera entre el Reino Unido y Chad: sus datos pueden circular entre ambos ' +
          'países dentro de nuestros equipos. Nuestros servidores están ubicados en Europa. ' +
          'Toda transferencia fuera del país de recogida se acompaña de garantías ' +
          'contractuales adecuadas.',
        ar:
          'تعمل برينيم بين المملكة المتحدة وتشاد: قد تنتقل بياناتك بين البلدين داخل فرقنا. ' +
          'خوادمنا موجودة في أوروبا. وأي نقل خارج بلد الجمع تصاحبه ضمانات تعاقدية مناسبة.',
      },
    },
    {
      key: 'conservation',
      title: {
        fr: 'Durées de conservation',
        en: 'Retention periods',
        es: 'Plazos de conservación',
        ar: 'مدد الاحتفاظ',
      },
      body: {
        fr:
          'Les messages de contact sont conservés trois ans à compter du dernier échange. Les ' +
          'candidatures non retenues sont conservées deux ans, sauf demande de suppression ' +
          'anticipée. Les documents contractuels sont conservés dix ans, comme la ' +
          "réglementation comptable l'impose.",
        en:
          'Contact messages are kept for three years from the last exchange. Unsuccessful ' +
          'applications are kept for two years, unless you ask for earlier deletion. ' +
          'Contractual documents are kept for ten years, as accounting rules require.',
        es:
          'Los mensajes de contacto se conservan tres años desde el último intercambio. Las ' +
          'candidaturas no seleccionadas se conservan dos años, salvo solicitud de supresión ' +
          'anticipada. Los documentos contractuales se conservan diez años, como exige la ' +
          'normativa contable.',
        ar:
          'تُحفظ رسائل الاتصال ثلاث سنوات من آخر تواصل. وتُحفظ الطلبات غير المقبولة سنتين، ما ' +
          'لم تطلب حذفها قبل ذلك. وتُحفظ المستندات التعاقدية عشر سنوات، وفق ما تفرضه القواعد ' +
          'المحاسبية.',
      },
    },
    {
      key: 'droits',
      title: {
        fr: 'Vos droits',
        en: 'Your rights',
        es: 'Sus derechos',
        ar: 'حقوقك',
      },
      body: {
        fr:
          "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, " +
          "d'opposition et de portabilité sur vos données. Écrivez à {email} : nous répondons " +
          "sous un mois. Si la réponse ne vous satisfait pas, vous pouvez saisir {autorite}.",
        en:
          'You have the right to access, rectify, erase, restrict, object to and port your ' +
          'data. Write to {email}: we reply within one month. If our answer does not satisfy ' +
          'you, you may lodge a complaint with {autorite}.',
        es:
          'Usted tiene derecho de acceso, rectificación, supresión, limitación, oposición y ' +
          'portabilidad sobre sus datos. Escriba a {email}: respondemos en el plazo de un mes. ' +
          'Si la respuesta no le satisface, puede dirigirse a {autorite}.',
        ar:
          'لديك الحق في الوصول إلى بياناتك وتصحيحها ومحوها وتقييدها والاعتراض عليها ونقلها. ' +
          'راسلنا على {email}: نرد خلال شهر واحد. وإذا لم يُرضك الرد، يمكنك التقدم بشكوى إلى ' +
          '{autorite}.',
      },
    },
    {
      key: 'securite',
      title: {
        fr: 'Sécurité',
        en: 'Security',
        es: 'Seguridad',
        ar: 'الأمان',
      },
      body: {
        fr:
          'Le site est servi exclusivement en HTTPS. Les accès à nos serveurs sont ' +
          'authentifiés et journalisés, et les sauvegardes sont chiffrées. En cas de violation ' +
          'de données susceptible de vous exposer à un risque, vous seriez informé sans délai ' +
          "injustifié, conformément à {cadre}.",
        en:
          'The site is served over HTTPS only. Access to our servers is authenticated and ' +
          'logged, and backups are encrypted. Should a data breach be likely to put you at ' +
          'risk, you would be informed without undue delay, in line with {cadre}.',
        es:
          'El sitio se sirve exclusivamente por HTTPS. Los accesos a nuestros servidores están ' +
          'autenticados y registrados, y las copias de seguridad están cifradas. En caso de ' +
          'violación de datos que pueda exponerle a un riesgo, se le informaría sin dilación ' +
          'indebida, conforme a {cadre}.',
        ar:
          'يُقدَّم الموقع عبر HTTPS حصرًا. والوصول إلى خوادمنا موثَّق ومسجَّل، والنسخ ' +
          'الاحتياطية مشفَّرة. وفي حال حدوث خرق للبيانات قد يعرضك للخطر، ستُبلَّغ دون تأخير ' +
          'غير مبرر، وفقًا لـ {cadre}.',
      },
    },
  ],
};

const TERMS = {
  slug: 'terms',
  title: {
    fr: "Conditions générales d'utilisation",
    en: 'Terms of Use',
    es: 'Condiciones generales de uso',
    ar: 'شروط الاستخدام',
  },
  lead: {
    fr:
      "Ces conditions régissent l'accès au site de {entite} et son utilisation. En consultant " +
      'ce site, vous les acceptez sans réserve.',
    en:
      'These terms govern access to and use of the {entite} website. By browsing this site, ' +
      'you accept them without reservation.',
    es:
      'Estas condiciones rigen el acceso al sitio de {entite} y su uso. Al consultar este ' +
      'sitio, usted las acepta sin reservas.',
    ar:
      'تحكم هذه الشروط الوصول إلى موقع {entite} واستخدامه. وباستعراضك هذا الموقع، فإنك تقبلها ' +
      'دون تحفظ.',
  },
  sections: [
    {
      key: 'editeur',
      title: {
        fr: 'Éditeur du site',
        en: 'Site publisher',
        es: 'Editor del sitio',
        ar: 'ناشر الموقع',
      },
      body: {
        fr:
          'Le site est édité par {entite}, {adresse}, immatriculée sous le numéro ' +
          '{immatriculation}. Contact : {email}, {telephone}. Le directeur de la publication ' +
          'est Romain Hapi Sr.',
        en:
          'The site is published by {entite}, {adresse}, registered under number ' +
          '{immatriculation}. Contact: {email}, {telephone}. The publication director is ' +
          'Romain Hapi Sr.',
        es:
          'El sitio está editado por {entite}, {adresse}, inscrita con el número ' +
          '{immatriculation}. Contacto: {email}, {telephone}. El director de publicación es ' +
          'Romain Hapi Sr.',
        ar:
          'ينشر الموقع {entite}، {adresse}، المسجلة تحت الرقم {immatriculation}. ' +
          'للتواصل: {email}، {telephone}. مدير النشر هو رومان هابي.',
      },
    },
    {
      key: 'objet',
      title: {
        fr: 'Objet du site',
        en: 'Purpose of the site',
        es: 'Objeto del sitio',
        ar: 'الغرض من الموقع',
      },
      body: {
        fr:
          "Ce site présente les activités, les services et les réalisations de {entite}. Il " +
          'constitue une source d’information et un moyen de prise de contact ; il ne vaut ni ' +
          'offre contractuelle, ni engagement de résultat. Toute prestation fait l’objet d’un ' +
          'contrat distinct.',
        en:
          'This site presents the activities, services and work of {entite}. It is a source of ' +
          'information and a means of getting in touch; it is neither a contractual offer nor ' +
          'a guarantee of results. Any engagement is governed by a separate contract.',
        es:
          'Este sitio presenta las actividades, servicios y realizaciones de {entite}. ' +
          'Constituye una fuente de información y un medio de contacto; no supone oferta ' +
          'contractual ni compromiso de resultado. Toda prestación es objeto de un contrato ' +
          'independiente.',
        ar:
          'يعرض هذا الموقع أنشطة {entite} وخدماتها وأعمالها. وهو مصدر للمعلومات ووسيلة ' +
          'للتواصل؛ ولا يُعد عرضًا تعاقديًا ولا ضمانًا لنتيجة. وتخضع كل خدمة لعقد مستقل.',
      },
    },
    {
      key: 'acces',
      title: {
        fr: 'Accès au site',
        en: 'Access to the site',
        es: 'Acceso al sitio',
        ar: 'الوصول إلى الموقع',
      },
      body: {
        fr:
          "L'accès est libre et gratuit, hors coûts de connexion à votre charge. Nous nous " +
          'efforçons de maintenir le site disponible en permanence, sans pouvoir le garantir : ' +
          "une interruption peut survenir pour maintenance, mise à jour ou cause extérieure. " +
          "Aucune indemnité n'est due à ce titre.",
        en:
          'Access is free of charge, excluding connection costs, which are yours. We work to ' +
          'keep the site permanently available but cannot guarantee it: an interruption may ' +
          'occur for maintenance, an update or an external cause. No compensation is owed on ' +
          'that basis.',
        es:
          'El acceso es libre y gratuito, salvo los costes de conexión a su cargo. Procuramos ' +
          'mantener el sitio disponible de forma permanente, sin poder garantizarlo: puede ' +
          'producirse una interrupción por mantenimiento, actualización o causa externa. No se ' +
          'debe indemnización alguna por ello.',
        ar:
          'الوصول حر ومجاني، باستثناء تكاليف الاتصال التي تتحملها. نسعى إلى إبقاء الموقع ' +
          'متاحًا دائمًا دون أن نضمن ذلك: فقد ينقطع للصيانة أو التحديث أو لسبب خارجي. ولا ' +
          'يُستحق أي تعويض على هذا الأساس.',
      },
    },
    {
      key: 'propriete',
      title: {
        fr: 'Propriété intellectuelle',
        en: 'Intellectual property',
        es: 'Propiedad intelectual',
        ar: 'الملكية الفكرية',
      },
      body: {
        fr:
          "L'ensemble du site — textes, identité visuelle, logos, illustrations, code — est " +
          'protégé et demeure la propriété de {entite} ou de ses partenaires. Toute ' +
          'reproduction ou réutilisation, totale ou partielle, requiert notre accord écrit ' +
          'préalable. Les marques et visuels de projets clients appartiennent à leurs ' +
          'titulaires respectifs.',
        en:
          'The whole site — copy, visual identity, logos, illustrations, code — is protected ' +
          'and remains the property of {entite} or its partners. Any reproduction or reuse, ' +
          'in whole or in part, requires our prior written consent. Client project marks and ' +
          'visuals belong to their respective owners.',
        es:
          'El conjunto del sitio — textos, identidad visual, logotipos, ilustraciones, código ' +
          '— está protegido y sigue siendo propiedad de {entite} o de sus socios. Toda ' +
          'reproducción o reutilización, total o parcial, requiere nuestro acuerdo previo por ' +
          'escrito. Las marcas y visuales de proyectos de clientes pertenecen a sus titulares.',
        ar:
          'الموقع بكامله — النصوص والهوية البصرية والشعارات والرسوم والشيفرة — محمي ويظل ملكًا ' +
          'لـ {entite} أو لشركائها. ويستلزم أي استنساخ أو إعادة استخدام، كليًا أو جزئيًا، ' +
          'موافقتنا الخطية المسبقة. أما علامات ومرئيات مشاريع العملاء فتعود لأصحابها.',
      },
    },
    {
      key: 'usage',
      title: {
        fr: 'Utilisation autorisée',
        en: 'Permitted use',
        es: 'Uso permitido',
        ar: 'الاستخدام المسموح',
      },
      body: {
        fr:
          'Vous vous engagez à ne pas perturber le fonctionnement du site, à ne pas tenter ' +
          "d'accéder à des zones non publiques, à ne pas en extraire massivement le contenu et " +
          'à ne pas utiliser les formulaires à des fins de prospection non sollicitée. Tout ' +
          "usage abusif peut donner lieu à un blocage d'accès et à des poursuites.",
        en:
          'You agree not to disrupt the operation of the site, not to attempt to reach ' +
          'non-public areas, not to extract its content in bulk and not to use the forms for ' +
          'unsolicited prospecting. Any abuse may lead to access being blocked and to legal ' +
          'action.',
        es:
          'Usted se compromete a no perturbar el funcionamiento del sitio, a no intentar ' +
          'acceder a zonas no públicas, a no extraer masivamente su contenido y a no utilizar ' +
          'los formularios con fines de prospección no solicitada. Todo uso abusivo puede dar ' +
          'lugar al bloqueo del acceso y a acciones legales.',
        ar:
          'تتعهد بعدم تعطيل عمل الموقع، وعدم محاولة الوصول إلى مناطق غير عامة، وعدم استخراج ' +
          'محتواه بالجملة، وعدم استخدام النماذج لأغراض تسويق غير مرغوب فيه. وقد يؤدي أي ' +
          'استخدام تعسفي إلى حظر الوصول وإلى ملاحقة قانونية.',
      },
    },
    {
      key: 'liens',
      title: {
        fr: 'Contenus et liens externes',
        en: 'Content and external links',
        es: 'Contenidos y enlaces externos',
        ar: 'المحتوى والروابط الخارجية',
      },
      body: {
        fr:
          'Les informations publiées sont données à titre indicatif et peuvent évoluer sans ' +
          'préavis. Le site renvoie vers des ressources tierces, dont FaDel Delivery : nous ne ' +
          'maîtrisons ni leur contenu ni leurs pratiques, et leur consultation relève de votre ' +
          'seule responsabilité.',
        en:
          'Published information is given for guidance and may change without notice. The site ' +
          'links to third-party resources, including FaDel Delivery: we control neither their ' +
          'content nor their practices, and visiting them is your sole responsibility.',
        es:
          'La información publicada es orientativa y puede cambiar sin previo aviso. El sitio ' +
          'remite a recursos de terceros, entre ellos FaDel Delivery: no controlamos ni su ' +
          'contenido ni sus prácticas, y su consulta es responsabilidad exclusiva suya.',
        ar:
          'المعلومات المنشورة إرشادية وقد تتغير دون إشعار. ويحيل الموقع إلى موارد خارجية، ' +
          'منها فاديل ديليفري: ولا نتحكم في محتواها ولا في ممارساتها، وزيارتها على مسؤوليتك ' +
          'وحدك.',
      },
    },
    {
      key: 'responsabilite',
      title: {
        fr: 'Responsabilité',
        en: 'Liability',
        es: 'Responsabilidad',
        ar: 'المسؤولية',
      },
      body: {
        fr:
          '{entite} ne saurait être tenue responsable des dommages indirects résultant de ' +
          "l'accès au site ou de l'usage des informations qu'il contient. Il vous appartient " +
          'de protéger votre équipement contre toute forme de contamination. Rien dans ces ' +
          'conditions ne limite une responsabilité qui ne peut légalement être écartée.',
        en:
          '{entite} cannot be held liable for indirect damage arising from access to the site ' +
          'or use of the information it contains. It is for you to protect your equipment ' +
          'against any form of contamination. Nothing in these terms limits liability that ' +
          'cannot lawfully be excluded.',
        es:
          '{entite} no será responsable de los daños indirectos derivados del acceso al sitio ' +
          'o del uso de la información que contiene. Le corresponde a usted proteger su equipo ' +
          'frente a cualquier forma de contaminación. Nada en estas condiciones limita una ' +
          'responsabilidad que no pueda excluirse legalmente.',
        ar:
          'لا تتحمل {entite} مسؤولية الأضرار غير المباشرة الناجمة عن الوصول إلى الموقع أو ' +
          'استخدام المعلومات الواردة فيه. ويقع عليك حماية جهازك من أي شكل من أشكال التلوث. ' +
          'ولا يحد أي بند في هذه الشروط من مسؤولية لا يمكن استبعادها قانونًا.',
      },
    },
    {
      key: 'donnees',
      title: {
        fr: 'Données personnelles',
        en: 'Personal data',
        es: 'Datos personales',
        ar: 'البيانات الشخصية',
      },
      body: {
        fr:
          'Le traitement des données personnelles est décrit dans notre politique de ' +
          'confidentialité, qui fait partie intégrante des présentes conditions et respecte ' +
          '{cadre}.',
        en:
          'The processing of personal data is described in our privacy policy, which forms an ' +
          'integral part of these terms and complies with {cadre}.',
        es:
          'El tratamiento de los datos personales se describe en nuestra política de ' +
          'privacidad, que forma parte integrante de estas condiciones y respeta {cadre}.',
        ar:
          'توصف معالجة البيانات الشخصية في سياسة الخصوصية الخاصة بنا، وهي جزء لا يتجزأ من هذه ' +
          'الشروط وتلتزم بـ {cadre}.',
      },
    },
    {
      key: 'droit',
      title: {
        fr: 'Droit applicable et juridiction',
        en: 'Governing law and jurisdiction',
        es: 'Derecho aplicable y jurisdicción',
        ar: 'القانون الواجب التطبيق والاختصاص',
      },
      body: {
        fr:
          'Les présentes conditions sont régies par {loi}. À défaut de résolution amiable, tout ' +
          'litige relève de {juridiction}. Nous pouvons modifier ces conditions à tout moment ; ' +
          'la version applicable est celle publiée sur cette page au jour de votre visite.',
        en:
          'These terms are governed by {loi}. Failing an amicable settlement, any dispute falls ' +
          'to {juridiction}. We may amend these terms at any time; the applicable version is ' +
          'the one published on this page on the day of your visit.',
        es:
          'Estas condiciones se rigen por {loi}. A falta de acuerdo amistoso, todo litigio ' +
          'corresponde a {juridiction}. Podemos modificar estas condiciones en cualquier ' +
          'momento; la versión aplicable es la publicada en esta página el día de su visita.',
        ar:
          'تخضع هذه الشروط لـ {loi}. وفي حال تعذر التسوية الودية، يؤول أي نزاع إلى ' +
          '{juridiction}. ويجوز لنا تعديل هذه الشروط في أي وقت؛ والنسخة السارية هي المنشورة ' +
          'على هذه الصفحة يوم زيارتك.',
      },
    },
  ],
};

const DOCS = { privacy: PRIVACY, terms: TERMS };

/** Date de derniere revision, affichee en tete de document. */
export const LEGAL_UPDATED = '2026-09-11';

const UPDATED_LABEL = {
  fr: 'Dernière mise à jour',
  en: 'Last updated',
  es: 'Última actualización',
  ar: 'آخر تحديث',
};

/** Surtitre de rubrique, affiche au-dessus du titre du document. */
const KIND_LABEL = {
  fr: 'Informations légales',
  en: 'Legal information',
  es: 'Información legal',
  ar: 'معلومات قانونية',
};

/** Libelles courts, pour les barres de pied de page. */
const SHORT_LABEL = {
  privacy: {
    fr: 'Confidentialité',
    en: 'Privacy',
    es: 'Privacidad',
    ar: 'الخصوصية',
  },
  terms: { fr: 'CGU', en: 'Terms of Use', es: 'Condiciones de uso', ar: 'شروط الاستخدام' },
};

const TOC_LABEL = {
  fr: 'Sommaire',
  en: 'Contents',
  es: 'Índice',
  ar: 'المحتويات',
};

const NOTICE = {
  fr:
    'Ce document est susceptible d’évoluer. Pour toute question sur son contenu, écrivez-nous ' +
    'à {email}.',
  en: 'This document may change. For any question about its content, write to us at {email}.',
  es:
    'Este documento puede evolucionar. Para cualquier consulta sobre su contenido, escríbanos ' +
    'a {email}.',
  ar: 'قد يتغير هذا المستند. لأي سؤال بخصوص محتواه، راسلنا على {email}.',
};

/** Remplace les jetons `{cle}` par les valeurs de la fiche d'entite. */
const fill = (text, entity, lang) =>
  text.replace(/\{(\w+)\}/g, (whole, key) => {
    const value = entity[key];
    if (value === undefined) return whole;
    return typeof value === 'string' ? value : value[lang] || value.fr;
  });

/**
 * Assemble un document pret a afficher : titre, chapo et sections, dans la
 * langue demandee, avec les jetons resolus pour l'entite voulue.
 *
 * `lang` peut valoir une langue que le document ne couvre pas ; on retombe
 * alors sur le francais plutot que d'afficher un vide.
 */
export const buildLegal = (docKey, entityKey, lang) => {
  const doc = DOCS[docKey];
  const entity = ENTITIES[entityKey];
  if (!doc || !entity) return null;
  const pick = (entry) => entry[lang] || entry.fr;

  return {
    slug: doc.slug,
    title: pick(doc.title),
    lead: fill(pick(doc.lead), entity, lang),
    updatedLabel: pick(UPDATED_LABEL),
    updated: LEGAL_UPDATED,
    shortTitle: pick(SHORT_LABEL[docKey]),
    kindLabel: pick(KIND_LABEL),
    tocLabel: pick(TOC_LABEL),
    notice: fill(pick(NOTICE), entity, lang),
    entity,
    // Adresse deja resolue dans la langue du document : les pages affichent
    // celle-ci et n'ont pas a connaitre la forme interne de la fiche.
    address: pick(entity.adresse),
    sections: doc.sections.map((s) => ({
      key: s.key,
      title: pick(s.title),
      body: fill(pick(s.body), entity, lang),
    })),
  };
};
